import { generatePath, Navigate, useParams } from 'react-router-dom'

import { StickyBox } from 'components/Box'
import { CommonFormContextProps, SaveButton, useCommonFormContextProps } from 'components/Form'
import { PageHeader } from 'components/Header'
import Loading from 'components/Loading'
import Tabs, { getPage, Tab } from 'components/Tabs'
import { ConfirmationDialog, useConfirmationDialog } from 'features/ab-tests'
import { AbTest } from 'features/ab-tests/types/abTest'
import { Alert } from 'features/alerts/types/alert'
import { DomainAlerts } from 'features/inventory/domain'
import {
  AddEditDomainContext,
  AddEditDomainContextType,
} from 'features/inventory/domain/contexts/AddEditDomainContext'
import { useAbility } from 'providers/casl'
import { generateSelector } from 'providers/product-fruits/utils'
import { ROUTES } from 'routes'
import { ProductTab } from 'types/productTab'
import { useGetDomain } from '../../forms/GeneralForm/hooks/useGetDomain'
import { useGetFreshDomainAlerts } from '../../forms/GeneralForm/hooks/useGetFreshDomainAlerts'
import { DomainAlert } from '../../types/DomainAlert'
import { EditDomainUrlParams } from './types'
import { useProductTabs } from './useProductTabs'

export const EditDomain = (): React.JSX.Element => {
  const ability = useAbility()
  const { id: domainId, tab: currentTab, workspaceId } = useParams<EditDomainUrlParams>()
  const tabs: Tab<ProductTab>[] = useProductTabs()
  const { domain } = useGetDomain({ id: domainId })
  const freshDomainAlerts: Alert<DomainAlert>[] = useGetFreshDomainAlerts({ domainId })
  const { confirmationHandler, isDialogOpen, setIsDialogOpen } = useConfirmationDialog()

  const commonContextProps: CommonFormContextProps = useCommonFormContextProps({
    formId: `domain-form-edit`,
  })

  if (!workspaceId || !domainId) {
    return <Navigate to={ROUTES.BASE.NOT_FOUND} />
  } else if (!domain) {
    return <Loading />
  }

  const contextProps: AddEditDomainContextType = {
    domain,
    ...commonContextProps,
  }

  const canUpdateDomain: boolean = ability.can('update', 'Domain')

  const activeAbTestId: AbTest['id'] | undefined = domain.activeAbTest?.id

  return (
    <AddEditDomainContext.Provider value={contextProps}>
      <StickyBox>
        <AddEditDomainContext.Consumer>
          {(ctxProps: AddEditDomainContextType): React.JSX.Element => (
            <>
              <PageHeader
                actions={
                  <SaveButton
                    {...ctxProps}
                    {...(activeAbTestId && { onConfirmation: confirmationHandler })}
                    can={canUpdateDomain}
                  />
                }
                backTo={generatePath(ROUTES.INVENTORY.DOMAIN.LIST, { workspaceId })}
                title={domain.name}
                titleAdornment={{ end: <DomainAlerts alerts={freshDomainAlerts} /> }}
              />

              {activeAbTestId && (
                <ConfirmationDialog
                  activeAbTestId={activeAbTestId}
                  formProps={commonContextProps}
                  isOpen={isDialogOpen}
                  setOpen={setIsDialogOpen}
                />
              )}
            </>
          )}
        </AddEditDomainContext.Consumer>

        <Tabs
          path={ROUTES.INVENTORY.DOMAIN.EDIT}
          productFruitsSelector={generateSelector({ step: 2, tour: 2 })}
          tabs={tabs}
        />
      </StickyBox>

      {getPage<ProductTab>(tabs, currentTab, 'general')}
    </AddEditDomainContext.Provider>
  )
}
